// PartA4PlansAfterSchool.js
import React from 'react';
import { Input, Checkbox, Row, Col } from 'antd';

const { TextArea } = Input;

const PartA4PlansAfterSchool = ({ formData, handleInputChange, handleArrayInputChange }) => {

    // Helper to handle checkbox changes for single-select behavior if needed, or multi-select
    const handleCheckboxChange = (key, value) => {
        // For this specific form, it seems like single select might be appropriate for some, 
        // but the UI allows multiple. We'll assume standard checkbox behavior (true/false) 
        // or array based if the backend expects it. 
        // Based on previous patterns, let's assume specific keys for each checkbox or a shared state.
        // If the backend expects a string value for a group, we'd use Radio. 
        // But the image shows squares (checkboxes).
        // Let's assume individual boolean keys for simplicity unless we see array logic.
        // Actually, looking at the image "Any other, please specify" implies these might be individual fields.

        // We'll use a simple toggle for boolean fields
        handleInputChange(key, !formData[key]);
    };

    return (
        <div className="a4-page font-[poppins] bg-white p-8 mx-auto text-[#333333]" style={{ maxWidth: '100%', width: '100%' }}>

            {/* Header */}
            <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-[#F97316] mb-1">PART A (4)</h2>
                <h3 className="text-lg font-bold text-black">Plans after School</h3>
            </div>

            {/* Section 1: The next big step */}
            <div className="mb-8">
                <div className="bg-[#BFDBFE] p-3 font-bold text-lg text-black flex items-center mb-4">
                    <span className="mr-2 text-2xl">⚡</span> {/* Icon placeholder */}
                    The next big step of my life
                </div>

                <p className="font-semibold mb-4">After I finish school, I want to...</p>

                <div className="border border-blue-300 p-6 rounded-lg">
                    <div className="grid grid-cols-4 gap-4 text-center">
                        <div className="flex flex-col items-center gap-2">
                            <Checkbox
                                checked={formData.plan_college_general}
                                onChange={(e) => handleInputChange('plan_college_general', e.target.checked)}
                                className="scale-150"
                            />
                            <span className="text-sm font-medium">go to college for a general education</span>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <Checkbox
                                checked={formData.plan_skill_training}
                                onChange={(e) => handleInputChange('plan_skill_training', e.target.checked)}
                                className="scale-150"
                            />
                            <span className="text-sm font-medium">take up a skill training full time</span>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <Checkbox
                                checked={formData.plan_professional_institute}
                                onChange={(e) => handleInputChange('plan_professional_institute', e.target.checked)}
                                className="scale-150"
                            />
                            <span className="text-sm font-medium">prepare for my next step for admission in a professional institute/college</span>
                        </div>
                        <div className="flex flex-col items-center gap-2 w-full">
                            <Checkbox
                                checked={formData.plan_other}
                                onChange={(e) => handleInputChange('plan_other', e.target.checked)}
                                className="scale-150 mb-1"
                            />
                            <span className="text-sm font-medium w-full">
                                Any other, please specify
                                <Input
                                    className="mt-1 border-b-2 border-black border-t-0 border-x-0 rounded-none focus:shadow-none px-0 text-center"
                                    value={formData.plan_other_text}
                                    onChange={(e) => handleInputChange('plan_other_text', e.target.value)}
                                />
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Split Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Left Column: College/Institute Plans */}
                <div className="bg-[#DBEAFE] p-6 rounded-lg border border-blue-200">
                    <h4 className="font-bold text-base mb-4 border-b border-blue-300 pb-2">
                        I plan to go to college/Skill institute/professional institute
                    </h4>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">College/Skill Institute/Professional Institute:</label>
                            <Input
                                className="border-b border-gray-400 border-t-0 border-x-0 rounded-none bg-transparent px-0 focus:shadow-none"
                                value={formData.institute_name}
                                onChange={(e) => handleInputChange('institute_name', e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Course/Skill Training:</label>
                            <Input
                                className="border-b border-gray-400 border-t-0 border-x-0 rounded-none bg-transparent px-0 focus:shadow-none"
                                value={formData.course_name}
                                onChange={(e) => handleInputChange('course_name', e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-2">Type of Course:</label>
                            <div className="flex gap-4">
                                <Checkbox
                                    checked={formData.course_type_certificate}
                                    onChange={(e) => handleInputChange('course_type_certificate', e.target.checked)}
                                >
                                    Certificate
                                </Checkbox>
                                <Checkbox
                                    checked={formData.course_type_diploma}
                                    onChange={(e) => handleInputChange('course_type_diploma', e.target.checked)}
                                >
                                    Diploma
                                </Checkbox>
                                <Checkbox
                                    checked={formData.course_type_degree}
                                    onChange={(e) => handleInputChange('course_type_degree', e.target.checked)}
                                >
                                    Degree
                                </Checkbox>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Work Plans */}
                <div className="bg-[#DBEAFE] p-6 rounded-lg border border-blue-200">
                    <h4 className="font-bold text-base mb-4 border-b border-blue-300 pb-2">
                        I plan to pursue a skill-based course. I would like to work...
                    </h4>

                    <div className="space-y-3">
                        <Checkbox
                            className="text-sm font-medium flex items-center gap-2"
                            checked={formData.work_corporation}
                            onChange={(e) => handleInputChange('work_corporation', e.target.checked)}
                        >
                            in a corporation or private business
                        </Checkbox>
                        <Checkbox
                            className="text-sm font-medium flex items-center gap-2"
                            checked={formData.work_own_business}
                            onChange={(e) => handleInputChange('work_own_business', e.target.checked)}
                        >
                            in my own business
                        </Checkbox>
                        <Checkbox
                            className="text-sm font-medium flex items-center gap-2"
                            checked={formData.work_family_business}
                            onChange={(e) => handleInputChange('work_family_business', e.target.checked)}
                        >
                            in my family business
                        </Checkbox>
                        <Checkbox
                            className="text-sm font-medium flex items-center gap-2"
                            checked={formData.work_government}
                            onChange={(e) => handleInputChange('work_government', e.target.checked)}
                        >
                            in government
                        </Checkbox>

                        <div className="flex items-center gap-2 mt-2">
                            <Checkbox
                                checked={formData.work_other}
                                onChange={(e) => handleInputChange('work_other', e.target.checked)}
                            >
                                Any other
                            </Checkbox>
                            <Input
                                className="border-b border-gray-400 border-t-0 border-x-0 rounded-none bg-transparent px-0 focus:shadow-none flex-1"
                                value={formData.work_other_text}
                                onChange={(e) => handleInputChange('work_other_text', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Discussion Table */}
            <div>
                <p className="font-medium text-sm mb-2">Please discuss the following with your teacher/counsellor/parent/caregiver/peer. <span className="text-xs text-gray-500">(Just mention in points)</span></p>

                <div className="grid grid-cols-3 border border-gray-300 rounded-lg overflow-hidden">
                    {/* Header Row */}
                    <div className="bg-[#BFDBFE] p-3 text-center font-bold text-sm border-r border-gray-300 flex items-center justify-center">
                        What strengths or abilities will be most helpful for you in fulfilling this plan?
                    </div>
                    <div className="bg-[#BFDBFE] p-3 text-center font-bold text-sm border-r border-gray-300 flex items-center justify-center">
                        What will you need to improve in order to achieve this plan?
                    </div>
                    <div className="bg-[#BFDBFE] p-3 text-center font-bold text-sm flex items-center justify-center">
                        What challenges do you think you will face in fulfilling this plan?
                    </div>

                    {/* Input Row */}
                    <div className="bg-[#DBEAFE] p-0 border-r border-gray-300">
                        <TextArea
                            className="bg-transparent border-none resize-none h-64 p-4 focus:shadow-none"
                            value={formData.discussion_strengths}
                            onChange={(e) => handleInputChange('discussion_strengths', e.target.value)}
                        />
                    </div>
                    <div className="bg-[#DBEAFE] p-0 border-r border-gray-300">
                        <TextArea
                            className="bg-transparent border-none resize-none h-64 p-4 focus:shadow-none"
                            value={formData.discussion_improvements}
                            onChange={(e) => handleInputChange('discussion_improvements', e.target.value)}
                        />
                    </div>
                    <div className="bg-[#DBEAFE] p-0">
                        <TextArea
                            className="bg-transparent border-none resize-none h-64 p-4 focus:shadow-none"
                            value={formData.discussion_challenges}
                            onChange={(e) => handleInputChange('discussion_challenges', e.target.value)}
                        />
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PartA4PlansAfterSchool;